import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const VIEW_PARAM = 'view';
const REPORTS_VIEW = 'reports';
const REPORT_PARAM = 'report';
// Share links predate the report viewer and open it with `?report=true`.
const LEGACY_OPEN_VALUE = 'true';

export interface ReportsViewState {
  isOpen: boolean;
  /** SOPInstanceUID of the report to show; undefined = the newest one. */
  selectedSop?: string;
  open: (sopInstanceUid?: string) => void;
  close: () => void;
  select: (sopInstanceUid: string) => void;
}

/**
 * Whether the PDF report viewer covers the images, and which report it shows,
 * kept in the URL (`?view=reports&report=<sop>`) so it survives a reload and
 * can be linked. Updates replace the history entry and keep other params
 * (`section`, the share hash `s`, …).
 */
const useReportsViewState = (): ReportsViewState => {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportParam = searchParams.get(REPORT_PARAM);
  const isOpen = searchParams.get(VIEW_PARAM) === REPORTS_VIEW || reportParam === LEGACY_OPEN_VALUE;
  const selectedSop = reportParam && reportParam !== LEGACY_OPEN_VALUE ? reportParam : undefined;

  const update = useCallback(
    (open: boolean, sopInstanceUid?: string) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (open) {
            next.set(VIEW_PARAM, REPORTS_VIEW);
          } else {
            next.delete(VIEW_PARAM);
          }
          if (open && sopInstanceUid) {
            next.set(REPORT_PARAM, sopInstanceUid);
          } else if (!open || next.get(REPORT_PARAM) === LEGACY_OPEN_VALUE) {
            next.delete(REPORT_PARAM);
          }
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const open = useCallback((sopInstanceUid?: string) => update(true, sopInstanceUid), [update]);
  const close = useCallback(() => update(false), [update]);
  const select = useCallback((sopInstanceUid: string) => update(true, sopInstanceUid), [update]);

  return useMemo(
    () => ({ isOpen, selectedSop, open, close, select }),
    [isOpen, selectedSop, open, close, select],
  );
};

export default useReportsViewState;
